import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, BadgeCheck, ExternalLink, FilePenLine, Flag, MapPin } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "@/components/admin/AdminShell";
import { Kpi, Skeleton, fmt } from "@/pages/admin/AdminPages";
import api, { apiErrorMessage } from "@/lib/api";

const STATUS_CLS = {
  active: "border-emerald-300 bg-emerald-50 text-emerald-700",
  draft: "border-brandyellow/50 bg-amber-50 text-[#B47500]",
  superseded: "border-gray-200 bg-gray-100 text-gray-600",
};
export const StatusPill = ({ status }) => (
  <span className={`rounded border px-2.5 py-0.5 text-xs capitalize ${STATUS_CLS[status] || STATUS_CLS.superseded}`}>{status}</span>
);

const condLabel = (c) => Object.entries(c || {}).map(([k, v]) => `${k.replace(/_/g, " ")}: ${Array.isArray(v) ? v.join(" / ") : String(v)}`);

export function KnowledgeHome() {
  const [d, setD] = useState(null);
  const [research, setResearch] = useState([]);
  useEffect(() => { api.get("/admin/knowledge/tree").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  useEffect(() => { api.get("/admin/knowledge/research").then((r) => setResearch(r.data)).catch(() => {}); }, []);

  return (
    <AdminShell title="Knowledge portal" subtitle="Corridors, requirement sets, sources and verification status">
      {d === null && <Skeleton />}
      {d === false && <p className="text-sm text-coral">Could not load knowledge data.</p>}
      {d && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="knowledge-kpis">
            <Kpi testId="kb-kpi-active" label="Active requirement sets" value={d.totals.active_sets} sub={`${d.totals.sets} versions in total`} />
            <Kpi testId="kb-kpi-stale" label={`Needs re-verification (>${d.stale_days}d)`} value={d.totals.stale} sub="Active sets past freshness window" />
            <Kpi testId="kb-kpi-flagged" label="Flagged for review" value={d.totals.flagged} sub="Sets with an open flag" />
            <Kpi testId="kb-kpi-drafts" label="Open drafts" value={d.totals.drafts} sub="Unpublished edits" />
          </div>

          {d.corridors.map((c) => (
            <section key={c.key} className="rounded-md border border-hborder bg-white" data-testid={`kb-corridor-${c.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hborder px-5 py-4">
                <h2 className="font-serif text-xl text-heading">{c.flag} {c.destination}{c.origin_country ? <span className="ml-2 text-sm font-sans text-muted">from {c.origin_country}</span> : null}</h2>
                <span className="rounded bg-ltblue px-2.5 py-1 text-xs text-tblue">{c.availability}</span>
              </div>
              <div className="divide-y divide-hborder/70">
                {c.categories.map((cat) => (
                  <div key={cat.key} className="px-5 py-3">
                    <p className="text-sm font-medium text-heading">{cat.name}</p>
                    {cat.sets.length === 0 ? <p className="mt-1 text-xs text-muted">No requirement set configured yet.</p> : (
                      <div className="mt-2 space-y-2">
                        {cat.sets.map((s) => (
                          <Link key={s.id} to={`/admin/knowledge/sets/${s.id}`} data-testid={`kb-set-${c.key}-${cat.key}-v${s.version}`}
                            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-hborder/80 px-4 py-2.5 text-sm transition-colors hover:bg-softcream">
                            <div className="flex items-center gap-3"><span className="font-medium text-heading">v{s.version}</span><StatusPill status={s.status} />
                              {s.origin === "admin" && <span className="text-xs text-muted">edited in admin</span>}</div>
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted">
                              <span>{s.requirement_count} requirements{s.conditional_count ? ` · ${s.conditional_count} conditional` : ""}</span>
                              <span className={s.status === "active" && s.stale ? "font-medium text-coral" : ""}>
                                {s.last_verified ? `Verified ${s.last_verified} (${s.days_since_verified}d ago)` : "Never verified"}</span>
                              {s.flagged && <span className="flex items-center gap-1 font-medium text-coral"><Flag size={12} /> Flagged</span>}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {c.jurisdictions?.length > 0 && (
                <div className="border-t border-hborder bg-softcream/60 px-5 py-4" data-testid={`kb-jurisdictions-${c.key}`}>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted"><MapPin size={13} /> Jurisdiction map (by province of residence)</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    {c.jurisdictions.map((j) => (
                      <div key={j.key} className="rounded-lg border border-hborder bg-white p-3 text-sm">
                        <p className="font-medium text-heading">{j.mission}</p>
                        <p className="mt-1 text-xs text-muted">{j.provinces.join(", ")}</p>
                        <p className="mt-1 text-xs text-muted">Submission centres: {j.submission_centres.join(", ")}</p>
                      </div>
                    ))}
                  </div>
                  {c.submission_provider && <p className="mt-3 text-xs text-muted">Submission provider: <b>{c.submission_provider.name}</b> — {c.submission_provider.service_fee}</p>}
                </div>
              )}
            </section>
          ))}

          {research.length > 0 && (
            <section className="border border-hborder bg-white" data-testid="research-log">
              <div className="border-b border-hborder px-5 py-4">
                <h2 className="font-serif text-xl text-heading">Research log</h2>
                <p className="mt-1 text-xs text-muted">Official pages the requirement sets were built from. Re-check each page when verifying a set.</p>
              </div>
              <ul className="divide-y divide-hborder/70">
                {research.map((r) => (
                  <li key={r.key} className="px-5 py-3 text-sm" data-testid={`research-${r.key}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <a href={r.url} target="_blank" rel="noreferrer" className="font-medium text-heading hover:underline">{r.title}</a>
                      <span className="text-xs text-muted">Fetched {r.fetched_on}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted">{r.finding}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </AdminShell>
  );
}

export function DiffView({ diff }) {
  if (!diff) return null;
  const none = !diff.added.length && !diff.removed.length && !diff.changed.length;
  return (
    <div className="space-y-3 text-sm" data-testid="kb-diff-result">
      <p className="text-xs text-muted">Comparing v{diff.a.version} → v{diff.b.version}</p>
      {none && <p className="rounded-lg bg-softcream p-3 text-muted">No differences in requirements.</p>}
      {diff.added.map((r) => <div key={r.code} className="rounded-lg border border-emerald-300 bg-emerald-50 p-3"><b className="text-emerald-700">Added</b> · {r.title} <span className="text-xs text-muted">({r.code}){r.applicant_conditions ? ` · if ${condLabel(r.applicant_conditions).join("; ")}` : ""}</span></div>)}
      {diff.removed.map((r) => <div key={r.code} className="rounded-lg border border-red-300 bg-red-50 p-3"><b className="text-red-700">Removed</b> · {r.title} <span className="text-xs text-muted">({r.code})</span></div>)}
      {diff.changed.map((r) => (
        <div key={r.code} className="rounded-lg border border-brandyellow/50 bg-amber-50 p-3">
          <b className="text-[#B47500]">Changed</b> · {r.title} <span className="text-xs text-muted">({r.code})</span>
          <ul className="mt-1 space-y-0.5 text-xs text-muted">
            {r.fields.map((f) => <li key={f}><b>{f}</b>: <s>{JSON.stringify(r.before[f])}</s> → {JSON.stringify(r.after[f])}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );
}

function FlagBox({ target, onSubmit, onCancel }) {
  const [note, setNote] = useState("");
  return (
    <div className="rounded-md border border-coral/40 bg-white p-4" data-testid="kb-flag-box">
      <p className="text-sm font-medium text-heading">Flag {target.code ? `requirement “${target.code}”` : "this set"} for review</p>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What needs checking? (required)" data-testid="kb-flag-note"
        className="mt-2 w-full rounded-lg border border-hborder p-2 text-sm outline-none focus:border-tblue" />
      <div className="mt-2 flex gap-2">
        <button onClick={() => onSubmit(note)} data-testid="kb-flag-submit" className="rounded-md bg-coral px-3 py-1.5 text-xs font-medium text-white">Flag</button>
        <button onClick={onCancel} className="rounded-md border border-hborder px-3 py-1.5 text-xs">Cancel</button>
      </div>
    </div>
  );
}

export function KnowledgeSet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [flagTarget, setFlagTarget] = useState(null);
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/knowledge/sets/${id}`).then((r) => setD(r.data)).catch(() => setD(false));
  }, [id]);
  useEffect(() => { setD(null); setDiff(null); load(); }, [load]);

  const review = async (action, code, note) => {
    try {
      await api.post(`/admin/knowledge/${action}`, { set_id: id, requirement_code: code || null, note });
      toast.success(action === "verify" ? "Marked as verified" : "Flagged for review");
      setFlagTarget(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const openDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/knowledge/sets/${id}/draft`);
      navigate(`/admin/knowledge/drafts/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); setBusy(false); }
  };

  const compare = async (otherId) => {
    if (!otherId) return setDiff(null);
    try {
      const other = d.siblings.find((s) => s.id === otherId);
      const [a, b] = other.version < d.set.version ? [otherId, id] : [id, otherId];
      setDiff((await api.get("/admin/knowledge/diff", { params: { a, b } })).data);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const title = d ? `${d.destination} · ${d.category_name}` : "Requirement set";
  const s = d?.summary;
  return (
    <AdminShell title={title} subtitle={d ? `Version ${d.set.version} · ${d.set.origin_scope || ""}` : ""}
      actions={d && (
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/knowledge" className="rounded-lg border border-hborder bg-white px-3 py-2 text-sm">← All sets</Link>
          {d.set.status !== "superseded" && d.set.status !== "draft" && (
            <>
              <button onClick={() => review("verify", null)} data-testid="kb-verify-set" className="flex items-center gap-1.5 rounded-lg bg-tblue px-3 py-2 text-sm font-medium text-white"><BadgeCheck size={15} /> Mark verified today</button>
              <button onClick={() => setFlagTarget({})} data-testid="kb-flag-set" className="flex items-center gap-1.5 rounded-lg border border-coral/40 px-3 py-2 text-sm text-coral"><Flag size={15} /> Flag set</button>
            </>
          )}
          {d.set.status === "draft"
            ? <Link to={`/admin/knowledge/drafts/${id}`} data-testid="kb-open-editor" className="flex items-center gap-1.5 rounded-lg bg-navy px-3 py-2 text-sm font-medium text-white"><FilePenLine size={15} /> Open draft editor</Link>
            : <button onClick={openDraft} disabled={busy} data-testid="kb-create-draft" className="flex items-center gap-1.5 rounded-lg bg-navy px-3 py-2 text-sm font-medium text-white disabled:opacity-60"><FilePenLine size={15} /> Edit as new draft</button>}
        </div>
      )}>
      {d === null && <Skeleton />}
      {d === false && <p className="text-sm text-coral">Requirement set not found.</p>}
      {d && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi testId="kb-set-status" label="Status" value={<StatusPill status={d.set.status} />} sub={d.set.effective_date ? `Effective ${d.set.effective_date}` : ""} />
            <Kpi testId="kb-set-count" label="Requirements" value={s.requirement_count} sub={`${s.conditional_count} conditional`} />
            <Kpi testId="kb-set-verified" label="Last verified" value={s.last_verified || "Never"} sub={s.days_since_verified != null ? <span className={s.stale ? "font-medium text-coral" : ""}>{s.days_since_verified} days ago</span> : ""} />
            <Kpi testId="kb-set-fee" label="Visa fee" value={<span className="text-xl">{d.set.visa_fee || "—"}</span>} sub={s.flagged ? <span className="font-medium text-coral">Open flag on set</span> : ""} />
          </div>

          {flagTarget && <FlagBox target={flagTarget} onCancel={() => setFlagTarget(null)} onSubmit={(n) => review("flag", flagTarget.code, n)} />}

          <section className="overflow-x-auto rounded-md border border-hborder bg-white hv-scroll-thin">
            <table className="w-full min-w-[860px] text-left text-sm" data-testid="kb-requirements-table">
              <thead className="border-b border-hborder bg-softcream text-xs uppercase tracking-wider text-muted">
                <tr><th className="px-4 py-3">Requirement</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Applies when</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Review</th><th className="px-4 py-3" /></tr>
              </thead>
              <tbody>
                {d.requirements.map((r) => (
                  <tr key={r.code} className="border-b border-hborder/60 align-top last:border-0" data-testid={`kb-req-${r.code}`}>
                    <td className="max-w-md px-4 py-3"><p className="font-medium text-heading">{r.title}</p><p className="text-xs text-muted">{r.code} · {r.category}</p><p className="mt-1 text-xs text-muted">{r.description}</p></td>
                    <td className="px-4 py-3 text-xs"><p>{r.required ? "Required" : "Recommended"}</p><p className="text-muted">{r.certainty}</p></td>
                    <td className="px-4 py-3">{r.applicant_conditions ? <div className="flex flex-wrap gap-1">{condLabel(r.applicant_conditions).map((c) => <span key={c} className="rounded bg-ltblue px-2 py-0.5 text-[11px] text-tblue">{c}</span>)}</div> : <span className="text-xs text-muted">Everyone</span>}</td>
                    <td className="px-4 py-3 text-xs">{r.source?.url ? <a href={r.source.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-tblue hover:underline">{r.source.name?.slice(0, 32)}<ExternalLink size={11} /></a> : "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {r.review?.flagged ? <span className="font-medium text-coral" title={r.review.flag_note}>Flagged: {r.review.flag_note}</span>
                        : r.review?.verified_at ? <span className="text-emerald-700">Verified {fmt(r.review.verified_at)}</span> : <span className="text-muted">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {d.set.status === "active" && (
                        <div className="flex justify-end gap-1.5">
                          <button onClick={() => review("verify", r.code)} data-testid={`kb-verify-${r.code}`} className="rounded border border-tblue/50 px-2 py-1 text-[11px] text-tblue hover:bg-ltblue">Verify</button>
                          <button onClick={() => setFlagTarget({ code: r.code })} data-testid={`kb-flag-${r.code}`} className="rounded border border-coral/40 px-2 py-1 text-[11px] text-coral hover:bg-coral/5">Flag</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {d.coverage.applicable && (
            <section className="rounded-md border border-hborder bg-white p-5" data-testid="kb-coverage">
              <h2 className="font-serif text-lg text-heading">Condition coverage</h2>
              <p className="text-xs text-muted">Which applicant conditions have at least one requirement attached.</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {d.coverage.matrix.map((m) => (
                  <div key={`${m.condition}-${m.value}`} className={`rounded-lg border p-3 text-sm ${m.covered ? "border-emerald-300 bg-emerald-50/50" : "border-brandyellow/50 bg-amber-50"}`}>
                    <p className="font-medium text-heading">{m.condition.replace(/_/g, " ")}: {String(m.value)}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-muted">{m.covered ? `${m.requirements.length} requirement(s): ${m.requirements.join(", ")}` : <><AlertTriangle size={12} className="text-[#B47500]" /> No requirement yet</>}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <section className="rounded-md border border-hborder bg-white p-5" data-testid="kb-diff-panel">
              <h2 className="font-serif text-lg text-heading">Version diff</h2>
              {d.siblings.length < 2 ? <p className="mt-2 text-sm text-muted">Only one version exists.</p> : (
                <>
                  <select onChange={(e) => compare(e.target.value)} defaultValue="" data-testid="kb-diff-select" className="mt-2 rounded-lg border border-hborder bg-white px-3 py-2 text-sm">
                    <option value="">Compare with…</option>
                    {d.siblings.filter((x) => x.id !== id).map((x) => <option key={x.id} value={x.id}>v{x.version} ({x.status})</option>)}
                  </select>
                  <div className="mt-3"><DiffView diff={diff} /></div>
                </>
              )}
            </section>
            <section className="rounded-md border border-hborder bg-white p-5">
              <h2 className="font-serif text-lg text-heading">Sources</h2>
              <ul className="mt-2 space-y-1.5 text-sm">
                {d.sources.map((x) => <li key={x.url}><a href={x.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-tblue hover:underline">{x.name}<ExternalLink size={12} /></a></li>)}
              </ul>
              <h2 className="mt-5 font-serif text-lg text-heading">Review history</h2>
              {d.history.length === 0 ? <p className="mt-2 text-sm text-muted">No reviews recorded yet.</p> : (
                <ul className="mt-2 divide-y divide-hborder/70" data-testid="kb-history">
                  {d.history.slice(0, 10).map((h) => (
                    <li key={h.id} className="py-2 text-xs"><b className={h.action === "flagged" ? "text-coral" : "text-emerald-700"}>{h.action}</b> {h.entity_type === "requirement" ? `· ${h.entity_id}` : "· set"} by {h.actor_name} <span className="text-muted">· {fmt(h.ts)}</span>{h.note && <p className="text-muted">“{h.note}”</p>}</li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
